/**
 * Today's date where the workspace is, not where the browser is.
 *
 * The server dates every period in the workspace's timezone: the seeded and fetched series, the
 * running period's projection (MetricSeriesService) and the converted report's running column. A
 * page that picks its current period from the browser's own date draws a period the server has not
 * started, empty and unprojected, whenever the two calendars disagree: a UTC browser on a Pacific
 * workspace every evening, and a whole new week on Sunday evenings.
 *
 * With no timezone, or one the browser does not know, it is the browser's own date, as the page
 * read it before this helper existed. When the browser is in the workspace's timezone the two are
 * the same date.
 */

const formatters = new Map();

function formatterFor(timeZone) {
    if (!formatters.has(timeZone)) {
        let formatter = null;

        try {
            formatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch (error) {
            if (!(error instanceof RangeError)) {
                throw error;
            }
            // Convention exception: a caught error that is warned about, not rethrown.
            // A name PHP accepts can still be unknown to an older browser's timezone data; throwing
            // here would blank the whole scorecard, so it keeps the browser's date, as it always has.
            console.warn('Unknown workspace timezone; using the browser\'s date', timeZone);
        }

        formatters.set(timeZone, formatter);
    }

    return formatters.get(timeZone);
}

function browserDateString(now) {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** The workspace's date as YYYY-MM-DD, the shape the page keys its periods by. */
export function workspaceDateString(timeZone, now = new Date()) {
    const formatter = timeZone ? formatterFor(timeZone) : null;

    if (formatter === null) {
        return browserDateString(now);
    }

    const parts = formatter.formatToParts(now);
    const part = (type) => parts.find((candidate) => candidate.type === type).value;

    return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * The workspace's date at the browser's local midnight: the shape the scorecard's period arithmetic
 * works in (getFullYear(), getMonth(), getDate(), setDate()), so it reads the workspace's calendar.
 */
export function workspaceToday(timeZone, now = new Date()) {
    return new Date(`${workspaceDateString(timeZone, now)}T00:00:00`);
}
